/**
 * Handlebars-lite template engine for SmartBlock kind === 'custom'.
 *
 * Intentionally tiny — supports just the syntax authors actually need to
 * format spec content: variable interpolation, conditionals, loops, and a
 * fixed set of helpers. No partial includes, no programmatic registration,
 * no helper-of-helper. If the syntax surface grows, replace with a vetted
 * dependency.
 *
 * Supported syntax:
 *   {{field}}                       → value lookup
 *   {{nested.field}}                → dot-path lookup
 *   {{#if field}} ... {{/if}}       → conditional (truthy)
 *   {{#each items}} ... {{/each}}   → iteration; inside, `this`, `@index`,
 *                                     and field names refer to current item
 *   {{join items "sep"}}            → comma-join helper
 *   {{upper field}} / {{lower field}}
 *
 * All values are HTML-safe by virtue of being treated as plain text —
 * the engine never returns HTML; resolvers produce ContentBlocks where
 * `text` is plain.
 */

type Scope = Record<string, any>;

const LOOKUP_RE = /\{\{\s*([^}]+?)\s*\}\}/g;

export interface TemplateRenderResult {
  /** The expanded plain text. Multiple lines stay newline-separated. */
  text: string;
  /** Whether anything failed to resolve — used for the "unresolved" badge. */
  hadErrors: boolean;
  /** Specific keys that didn't resolve, for debugging in the editor. */
  missingKeys: string[];
}

export function renderTemplate(template: string, scope: Scope): TemplateRenderResult {
  const missingKeys: string[] = [];
  let text: string;
  try {
    text = expand(template, scope, missingKeys);
  } catch (err: any) {
    return {
      text: `[Template error: ${err.message}]`,
      hadErrors: true,
      missingKeys,
    };
  }
  return { text, hadErrors: missingKeys.length > 0, missingKeys };
}

function expand(template: string, scope: Scope, missing: string[]): string {
  // Block syntax (if / each) handled first so their bodies don't get
  // single-pass interpolated incorrectly.
  let out = expandBlocks(template, scope, missing);
  out = expandLeaves(out, scope, missing);
  return out;
}

function expandBlocks(input: string, scope: Scope, missing: string[]): string {
  let prev: string;
  let out = input;
  // Repeat until stable — nested {{#each}} inside {{#if}} get expanded.
  do {
    prev = out;
    out = out.replace(
      /\{\{#each\s+([^}]+?)\s*\}\}([\s\S]*?)\{\{\/each\}\}/g,
      (_, listExpr, body) => {
        const list = lookup(listExpr.trim(), scope, missing);
        if (!Array.isArray(list)) return '';
        return list
          .map((item, idx) => {
            const childScope = {
              ...scope,
              this: item,
              '@index': idx,
              ...(typeof item === 'object' && item !== null ? item : {}),
            };
            return expand(body, childScope, missing);
          })
          .join('');
      }
    );
    out = out.replace(
      /\{\{#if\s+([^}]+?)\s*\}\}([\s\S]*?)(?:\{\{else\}\}([\s\S]*?))?\{\{\/if\}\}/g,
      (_, condExpr, truthy, falsy = '') => {
        const v = lookup(condExpr.trim(), scope, missing);
        return v ? expand(truthy, scope, missing) : expand(falsy, scope, missing);
      }
    );
  } while (out !== prev);
  return out;
}

function expandLeaves(input: string, scope: Scope, missing: string[]): string {
  return input.replace(LOOKUP_RE, (_, expr: string) => {
    const trimmed = expr.trim();
    // Helpers
    const helperMatch = trimmed.match(/^(\w+)\s+(.+)$/);
    if (helperMatch && HELPERS[helperMatch[1]]) {
      return HELPERS[helperMatch[1]](helperMatch[2], scope, missing);
    }
    const v = lookup(trimmed, scope, missing);
    return v === undefined || v === null ? '' : String(v);
  });
}

function lookup(path: string, scope: Scope, missing: string[]): any {
  // Quoted string literal
  const q = path.match(/^"([^"]*)"$/) || path.match(/^'([^']*)'$/);
  if (q) return q[1];
  // Number literal
  if (/^-?\d+(?:\.\d+)?$/.test(path)) return Number(path);
  const segments = path.split('.');
  let cur: any = scope;
  for (const seg of segments) {
    if (cur === null || cur === undefined) {
      missing.push(path);
      return undefined;
    }
    cur = cur[seg];
  }
  if (cur === undefined) missing.push(path);
  return cur;
}

const HELPERS: Record<string, (args: string, scope: Scope, missing: string[]) => string> = {
  join: (args, scope, missing) => {
    const parts = splitArgs(args);
    const list = lookup(parts[0], scope, missing);
    const sep = parts[1] ? String(lookup(parts[1], scope, missing) ?? ', ') : ', ';
    if (!Array.isArray(list)) return '';
    return list.join(sep);
  },
  upper: (args, scope, missing) => {
    const v = lookup(args.trim(), scope, missing);
    return v === undefined || v === null ? '' : String(v).toUpperCase();
  },
  lower: (args, scope, missing) => {
    const v = lookup(args.trim(), scope, missing);
    return v === undefined || v === null ? '' : String(v).toLowerCase();
  },
  count: (args, scope, missing) => {
    const v = lookup(args.trim(), scope, missing);
    return Array.isArray(v) ? String(v.length) : '0';
  },
};

function splitArgs(args: string): string[] {
  // Split on whitespace but respect quoted strings
  const out: string[] = [];
  let cur = '';
  let inQuote: string | null = null;
  for (let i = 0; i < args.length; i++) {
    const c = args[i];
    if (inQuote) {
      cur += c;
      if (c === inQuote) inQuote = null;
    } else if (c === '"' || c === "'") {
      cur += c;
      inQuote = c;
    } else if (/\s/.test(c)) {
      if (cur) {
        out.push(cur);
        cur = '';
      }
    } else {
      cur += c;
    }
  }
  if (cur) out.push(cur);
  return out;
}
